import { Divider, FormGroup, InputGroup, Switch, Tab, Tabs, } from "@blueprintjs/core";
import { MDBCol, } from "mdbreact";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import PhoneInput from "react-phone-input-2";
import PhoneNumberInput from "../utils/PhoneNumberInput";

interface EmpresaNotificacoesProps {
  goToTab: (tabId: string) => void;
}

interface EmpresaNotificacoesState {
}

  /**
   * Componente responsável pela configuração das notificações da empresa.
   *
   * Este componente renderiza uma interface em abas para permitir a configuração
   * de diferentes tipos de notificações, como:
   *
   * - Caixa Postal RFB;
   * - DEC SEFA;
   * - DET;
   * - Parcelamentos;
   * - DAS / PGDAS-D;
   * - Alertas de cálculo;
   * - CNDS.
   *
   * As configurações são lidas e atualizadas diretamente a partir de
   * `EmpresaStore.obs.empresa_edit`, permitindo que os campos editados na tela
   * sejam refletidos no estado global da empresa.
   *
   * O componente utiliza:
   *
   * - `Tabs` e `Tab` do BlueprintJS para organizar as configurações;
   * - `Switch` para ativar/desativar notificações;
   * - `InputGroup` para campos de e-mail;
   * - `PhoneNumberInput` para listas de números de WhatsApp;
   * - `PhoneInput` para campos de telefone simples;
   * - `MobX` para observação e atualização reativa da interface.
   *
   * Algumas abas possuem validações condicionais. Por exemplo:
   *
   * - Caixa Postal exige que `caixapostal_automatica` esteja ativo;
   * - DEC SEFA exige que `dec_sefa_auto` esteja ativo;
   * - DET exige que `det_auto` esteja ativo;
   * - PGDAS só é exibido para empresas do Simples Nacional e exige arquivo PGDAS-D importado.
  */

@observer
export default class EmpresaNotificacoes extends React.Component<EmpresaNotificacoesProps, EmpresaNotificacoesState> {

  obs = observable({
    selectedTab: "caixa_postal_notificacao" as string,
    max_numeros: 2 as number,
    max_numeros_whatsapp: 6 as number,
  })

  constructor(props) {
    super(props);
  }

  /**
   * Estado observável local do componente.
   *
   * `selectedTab` controla a aba atualmente selecionada dentro da tela de
   * notificações.
   *
   * `max_numeros` define a quantidade máxima de números de WhatsApp permitidos
   * nos campos que utilizam o componente `PhoneNumberInput`.
  */

  handleGoToTab = (tab: string) => {
    this.props.goToTab(tab);
  };

  /**
   * Renderiza uma mensagem com botão de atalho para outra aba do cadastro.
   *
   * Este recurso é utilizado quando determinada configuração depende de outra
   * automação estar previamente ativa. Por exemplo, se o monitoramento da Caixa
   * Postal não estiver ativo, o componente exibe uma mensagem orientando o usuário
   * a ir até a aba correspondente.
   *
   * @param mensagem Texto explicativo exibido ao usuário.
   * @param tabId Identificador da aba para onde o botão irá redirecionar.
   * @param tabLabel Nome amigável da aba exibido no botão.
   * @returns Elemento JSX contendo a mensagem e o botão de navegação.
  */

  renderAtalho(mensagem: string, tabId: string, tabLabel: string) {
    return (
      <MDBCol md="12">
        <p style={{ marginBottom: 8 }}>{mensagem}
          &nbsp;
          <button
            onClick={() => this.handleGoToTab(tabId)}
            style={{ padding: 0, border: "none", background: "none", fontWeight: "bold", textDecoration: "underline", color: "#3579ea", cursor: "pointer" }}
          >
            Ir para a aba {tabLabel}
          </button>
        </p>
      </MDBCol>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações da Caixa Postal RFB.
   *
   * Caso o monitoramento automático da Caixa Postal não esteja ativo
   * (`caixapostal_automatica`), é exibido um atalho para a aba "Caixa Postal".
   *
   * Quando ativo, permite configurar o envio de avisos por WhatsApp para novas
   * mensagens da Caixa Postal. Ao habilitar o switch, o usuário pode informar
   * até `max_numeros` números de telefone.
   *
   * Campos utilizados:
   *
   * - `caixapostal_automatica`: define se o monitoramento está habilitado;
   * - `send_message_caixa_postal`: define se avisos por WhatsApp serão enviados;
   * - `caixa_postal_numero`: lista de números que receberão as notificações.
   *
   * @returns Elemento JSX da aba Caixa Postal.
  */

  caixaPostalCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    if (!e.caixapostal_automatica) {
      return this.renderAtalho(
        "Ative o monitoramento da Caixa Postal (RFB) para poder configurar os avisos por WhatsApp.",
        "certificado",
        "Certificado",
      );
    }

    return (
      <>
        <MDBCol md="12">
          <Switch
            label="Receber aviso de novas mensagens do Caixa Postal por WhatsApp"
            alignIndicator="right"
            checked={e.send_message_caixa_postal}
            onChange={(ev: any) => e.send_message_caixa_postal = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />
        </MDBCol>

        {e.send_message_caixa_postal ? (
          <FormGroup helperText={`Pressione Enter a cada número informando para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber avisos de novas mensagens.`}>
            <PhoneNumberInput
              value={e.caixa_postal_numero}
              onChange={(phones) => e.caixa_postal_numero = phones}
              maxPhones={this.obs.max_numeros}
              placeholder="+55 (11) 99999-9999"
              disabled={false}
            />
          </FormGroup>
        ) : <br />}
      </>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações do DEC SEFA.
   *
   * Caso o DEC SEFA automático não esteja ativo (`dec_sefa_auto`), exibe um
   * atalho para a aba "SEFA | e-CAC".
   *
   * Quando ativo, permite configurar o recebimento de avisos por WhatsApp para
   * novas mensagens do DEC SEFA.
   *
   * Campos utilizados:
   *
   * - `dec_sefa_auto`: define se a automação do DEC SEFA está ativa;
   * - `dec_sefa_message`: define se o envio por WhatsApp está ativo;
   * - `dec_sefa_numero`: lista de números que receberão os avisos.
   *
   * @returns Elemento JSX da aba DEC SEFA.
  */

  decSefaCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    if (!e.dec_sefa_auto) {
      return this.renderAtalho(
        "Ative o \"Dec Sefa automático\" para poder configurar os avisos por WhatsApp.",
        "sefa",
        "SEFA | e-CAC",
      );
    }

    return (
      <>
        <MDBCol md="12">
          <Switch
            label="Receber aviso de NOVAS MENSAGENS por WhatsApp"
            alignIndicator="right"
            checked={e.dec_sefa_message}
            onChange={(ev: any) =>
              e.dec_sefa_message = ev.target.checked
            }
            style={{
              width: "max-content",
              fontWeight: "bold",
            }}
          />

          {e.dec_sefa_message ? (
            <FormGroup
              helperText={`Pressione Enter a cada número informado para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber avisos de novas mensagens.`}
            >
              <PhoneNumberInput
                value={
                  typeof e.dec_sefa_numero === "string"
                    ? [e.dec_sefa_numero]
                    : e.dec_sefa_numero
                }
                onChange={(phones) =>
                  e.dec_sefa_numero = phones
                }
                maxPhones={this.obs.max_numeros}
                placeholder="+55 (11) 99999-9999"
                disabled={false}
              />
            </FormGroup>

          ) : (
            <br />
          )}
          <Divider />

        </MDBCol>

        {e.dec_type !== "api" && (
          <>
            <br />

            {this.renderAtalho(
              "Selecione \"Consultar DEC Sefa via API\" para configurar esta opção.",
              "sefa",
              "SEFA | e-CAC",
            )}
          </>
        )}

        {e.dec_type === "api" && (
          <>
            <MDBCol md="12">
              <Switch
                label="Receber aviso de PROCURAÇÕES por WhatsApp"
                alignIndicator="right"
                checked={e.send_procuracao_numero}
                onChange={(ev: any) =>
                  e.send_procuracao_numero = ev.target.checked
                }
                style={{
                  width: "max-content",
                  fontWeight: "bold",
                }}
              />

              {e.send_procuracao_numero ? (
                <FormGroup
                  helperText={`Pressione Enter a cada número informado para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber avisos de novas mensagens.`}
                >
                  <PhoneNumberInput
                    value={
                      typeof e.dec_sefa_procuracao_numero === "string"
                        ? [e.dec_sefa_procuracao_numero]
                        : e.dec_sefa_procuracao_numero
                    }
                    onChange={(phones) =>
                      e.dec_sefa_procuracao_numero = phones
                    }
                    maxPhones={this.obs.max_numeros}
                    placeholder="+55 (11) 99999-9999"
                    disabled={false}
                  />
                </FormGroup>
              ) : (
                <br />
              )}
              <Divider />

            </MDBCol>
            <MDBCol md="12">
              <Switch
                label="Receber um aviso da PROCURAÇÃO por E-mail"
                alignIndicator="right"
                checked={e.send_procuracao_email}
                onChange={(ev: any) => e.send_procuracao_email = ev.target.checked}
                style={{ width: "max-content", fontWeight: "bold" }}
              />

              {e.send_procuracao_email ? (
                <FormGroup helperText="Insira um e-mail para receber a notificação de procuração, para mais de um e-mail separar por vírgula.">
                  <InputGroup
                    type="text"
                    placeholder="email@email.com"
                    value={e.dec_sefa_procuracao_email}
                    onChange={(ev: any) => e.dec_sefa_procuracao_email = ev.target.value}>
                  </InputGroup>
                </FormGroup>
              ) : <br />}
            </MDBCol>
          </>
        )}
      </>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações do DET.
   *
   * Caso o DET automático não esteja ativo (`det_auto`), exibe um atalho para
   * a aba "SEFA | e-CAC".
   *
   * Quando ativo, permite configurar o recebimento de avisos por WhatsApp para
   * novas mensagens do DET.
   *
   * O campo `det_numero` pode vir como string em registros antigos. Por isso,
   * antes de enviar o valor ao `PhoneNumberInput`, o componente normaliza o valor
   * para array quando necessário.
   *
   * Campos utilizados:
   *
   * - `det_auto`: define se a automação do DET está ativa;
   * - `det_message`: define se os avisos por WhatsApp estão ativos;
   * - `det_numero`: número ou lista de números que receberão os avisos.
   *
   * @returns Elemento JSX da aba DET.
  */

  detCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    if (!e.det_auto) {
      return this.renderAtalho(
        "Ative o \"Det automático\" para poder configurar os avisos por WhatsApp.",
        "sefa",
        "SEFA | e-CAC",
      );
    }

    return (
      <MDBCol md="12">
        <Switch
          label="Receber aviso de NOVAS MENSAGENS por WhatsApp"
          alignIndicator="right"
          checked={e.det_message}
          onChange={(ev: any) => e.det_message = ev.target.checked}
          style={{ width: "max-content", fontWeight: "bold" }}
        />

        {e.det_message ? (
          <FormGroup helperText={`Pressione Enter a cada número informando para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber avisos de novas mensagens.`}>
            <PhoneNumberInput
              value={typeof e.det_numero == "string" ? [e.det_numero] : e.det_numero}
              onChange={(phones) => e.det_numero = phones}
              maxPhones={this.obs.max_numeros}
              placeholder="+55 (11) 99999-9999"
              disabled={false}
            />
          </FormGroup>
        ) : <br />}
      </MDBCol>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações de Parcelamentos.
   *
   * Esta aba permite configurar dois tipos de envio:
   *
   * - envio de cópias das parcelas por e-mail;
   * - envio de cópias das parcelas por WhatsApp.
   *
   * Quando o envio por e-mail é habilitado, o campo `parcelamentos_email` é
   * exibido para preenchimento.
   *
   * Quando o envio por WhatsApp é habilitado, o campo `parcelamentos_numero` é
   * exibido usando `PhoneNumberInput`, permitindo cadastrar até `max_numeros`
   * números.
   *
   * Campos utilizados:
   *
   * - `send_parcelas`: ativa/desativa envio por e-mail;
   * - `parcelamentos_email`: e-mail ou lista de e-mails separados por vírgula;
   * - `send_parcelas_message`: ativa/desativa envio por WhatsApp;
   * - `parcelamentos_numero`: número ou lista de números de WhatsApp.
   *
   * @returns Elemento JSX da aba Parcelamentos.
  */

  parcelamentosCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    return (
      <>
        <MDBCol md="12">
          <Switch
            label="Receber uma cópia das PARCELAS por E-mail"
            alignIndicator="right"
            checked={e.send_parcelas}
            onChange={(ev: any) => e.send_parcelas = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />

          {e.send_parcelas ? (
            <FormGroup helperText="Insira um e-mail para receber as cópias dos arquivos, para mais de um e-mail separar por vírgula.">
              <InputGroup
                type="text"
                placeholder="email@email.com"
                value={e.parcelamentos_email}
                onChange={(ev: any) => e.parcelamentos_email = ev.target.value}>
              </InputGroup>
            </FormGroup>
          ) : <br />}
        </MDBCol>

        <Divider />
        <br />

        <MDBCol md="12">
          <Switch
            label="Receber uma cópia das PARCELAS por WhatsApp"
            alignIndicator="right"
            checked={e.send_parcelas_message}
            onChange={(ev: any) => e.send_parcelas_message = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />

          {e.send_parcelas_message ? (
            <FormGroup helperText={`Pressione Enter a cada número informando para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber as cópias dos arquivos.`}>
              <PhoneNumberInput
                value={typeof e.parcelamentos_numero == "string" ? [e.parcelamentos_numero] : e.parcelamentos_numero}
                onChange={(phones) => e.parcelamentos_numero = phones}
                maxPhones={this.obs.max_numeros}
                placeholder="+55 (11) 99999-9999"
                disabled={false}
              />
            </FormGroup>
          ) : <br />}
        </MDBCol>
      </>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações de DAS / PGDAS-D.
   *
   * Esta aba possui regras condicionais:
   *
   * - empresas filiais não podem configurar notificações de DAS / PGDAS-D;
   * - a configuração só é liberada se existir PDF PGDAS-D importado;
   * - a aba PGDAS só é exibida no `render` quando a empresa é do Simples Nacional.
   *
   * Permite configurar notificações por e-mail e por WhatsApp para arquivos DAS
   * e PGDAS-D.
   *
   * Campos utilizados:
   *
   * - `filial`: impede o uso da funcionalidade para empresas filiais;
   * - `EmpresaStore.obs.existe_pdf_pgdas`: indica se há PDF PGDAS-D importado;
   * - `send_das`: ativa envio de DAS por e-mail;
   * - `send_pgdas`: ativa envio de PGDAS-D por e-mail;
   * - `pgdas_email`: e-mail de destino;
   * - `send_message_das`: ativa envio de DAS por WhatsApp;
   * - `send_message_pgdas`: ativa envio de PGDAS-D por WhatsApp;
   * - `pgdas_numero`: número ou lista de números de WhatsApp.
   *
   * @returns Elemento JSX da aba PGDAS.
  */

  pgdasCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    if (e.filial) {
      return (
        <MDBCol md="12">
          <p>As notificações de DAS / PGDAS-D estão disponíveis apenas para empresas Matriz.</p>
        </MDBCol>
      );
    }

    if (!EmpresaStore.obs.existe_pdf_pgdas) {
      return this.renderAtalho(
        "Importe o arquivo PGDAS-D para poder configurar as notificações de DAS / PGDAS-D.",
        "pgdas",
        "PGDAS",
      );
    }

    return (
      <>
        <MDBCol md="12">
          <Switch
            label="Receber uma cópia dos arquivos DAS por E-mail"
            alignIndicator="right"
            checked={e.send_das}
            onChange={(ev: any) => e.send_das = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />
        </MDBCol>

        <br />

        <MDBCol md="12">
          <Switch
            label="Receber uma cópia dos arquivos PGDAS-D por E-mail"
            alignIndicator="right"
            checked={e.send_pgdas}
            onChange={(ev: any) => e.send_pgdas = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />
        </MDBCol>

        {(e.send_das || e.send_pgdas) ? (
          <MDBCol md="12">
            <FormGroup
              label=""
              style={{ width: "60%" }}
              helperText="Insira um e-mail para receber as cópias dos arquivos."
            >
              <InputGroup
                type="text"
                placeholder="email@email.com"
                value={e.pgdas_email}
                onChange={(ev: any) => e.pgdas_email = ev.target.value}>
              </InputGroup>
            </FormGroup>
          </MDBCol>
        ) : <br />}

        <Divider />
        <br />

        <MDBCol md="12">
          <Switch
            label="Receber uma cópia dos arquivos DAS por WhatsApp"
            alignIndicator="right"
            checked={e.send_message_das}
            onChange={(ev: any) => e.send_message_das = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />
        </MDBCol>

        <br />

        <MDBCol md="12">
          <Switch
            label="Receber uma cópia dos arquivos PGDAS-D por WhatsApp"
            alignIndicator="right"
            checked={e.send_message_pgdas}
            onChange={(ev: any) => e.send_message_pgdas = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />
        </MDBCol>

        {(e.send_message_das || e.send_message_pgdas) ? (
          <MDBCol md="12">
            <FormGroup helperText={`Pressione Enter a cada número informando para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber as cópias dos arquivos.`}>
              <PhoneNumberInput
                value={typeof e.pgdas_numero == "string" ? [e.pgdas_numero] : e.pgdas_numero}
                onChange={(phones) => e.pgdas_numero = phones}
                maxPhones={this.obs.max_numeros}
                placeholder="+55 (11) 99999-9999"
                disabled={false}
              />
            </FormGroup>
          </MDBCol>
        ) : <br />}
      </>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações CNDS.
   *
   * Permite habilitar ou desabilitar o envio de avisos por WhatsApp relacionados
   * às certidões CNDS.
   *
   * Quando o switch `send_message_cnds` está ativo, o componente exibe um campo
   * para cadastro de até `max_numeros` números de telefone. Os números são
   * armazenados em `cnds_numero` e posteriormente utilizados pelo backend para
   * disparo das notificações.
   *
   * Campos utilizados:
   *
   * - `send_message_cnds`: ativa/desativa notificações CNDS por WhatsApp;
   * - `cnds_numero`: lista de números que receberão os avisos.
   *
   * @returns Elemento JSX da aba CNDS.
  */

  calculosCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    return (
      <MDBCol>
        <FormGroup
          label="E-mail Erros de Cálculo"
          style={{ width: "49%" }}
        >
          <InputGroup
            autoComplete="email-calculos"
            name="email-calculos"
            id="nome-fantasia-off"
            placeholder="email@email.com"
            value={e.email_calculos}
            onChange={(ev: any) => e.email_calculos = ev.target.value}
          />
        </FormGroup>

        <FormGroup
          label="E-mail Alerta notas com valor acima de 1 milhão"
          style={{ width: "49%" }}
        >
          <InputGroup
            autoComplete="email-alerta"
            name="email-alerta"
            id="nome-fantasia-off"
            placeholder="email@email.com"
            value={e.email_alerta}
            onChange={(ev: any) => e.email_alerta = ev.target.value}
          />
        </FormGroup>

        <FormGroup
          label="Whatsapp Alerta notas com valor acima de 1 milhão"
          helperText={`Pressione Enter a cada número informado para adicionar. `}
          style={{ width: 400 }}
        >
          <PhoneNumberInput
            key={Array.from(e.numero_alerta || []).join("-")}
            value={Array.from(e.numero_alerta || [])}
            onChange={(phones) => e.numero_alerta = phones}
            maxPhones={this.obs.max_numeros_whatsapp}
            placeholder="+55 (11) 99999-9999"
            disabled={false}
          />
        </FormGroup>
      </MDBCol>
    );
  }

  /**
   * Renderiza o conteúdo da aba de notificações CNDS.
   *
   * Permite habilitar ou desabilitar o envio de avisos por WhatsApp relacionados
   * às certidões CNDS.
   *
   * Quando o switch `send_message_cnds` está ativo, o componente exibe um campo
   * para cadastro de até `max_numeros` números de telefone. Os números são
   * armazenados em `cnds_numero` e posteriormente utilizados pelo backend para
   * disparo das notificações.
   *
   * Campos utilizados:
   *
   * - `send_message_cnds`: ativa/desativa notificações CNDS por WhatsApp;
   * - `cnds_numero`: lista de números que receberão os avisos.
   *
   * @returns Elemento JSX da aba CNDS.
  */

  cndsCorpo() {
    const e = EmpresaStore.obs.empresa_edit;

    return (

      <>

        <MDBCol md="12">
          <Switch
            label="Receber aviso de novas mensagens do CNDS por WhatsApp"
            alignIndicator="right"
            checked={e.send_message_cnds}
            onChange={(ev: any) => e.send_message_cnds = ev.target.checked}
            style={{ width: "max-content", fontWeight: "bold" }}
          />
        </MDBCol>

        {e.send_message_cnds ? (
          <FormGroup helperText={`Pressione Enter a cada número informando para adicionar. Insira até ${this.obs.max_numeros} números com DDD para receber avisos de novas mensagens.`}>
            <PhoneNumberInput
              value={e.cnds_numero}
              onChange={(phones) => e.cnds_numero = phones}
              maxPhones={this.obs.max_numeros}
              placeholder="+55 (11) 99999-9999"
              disabled={false}
            />
          </FormGroup>
        ) : <br />}

      </>
    );
  }

  render() {

    const e = EmpresaStore.obs.empresa_edit;

    return (
      <MDBCol>

        <Tabs
          id="tabs-notificacoes"
          className="tabsCalculos tabsEmpresaForm"
          selectedTabId={this.obs.selectedTab}
          onChange={(tabId) => this.obs.selectedTab = String(tabId)}
        >

          <Tab
            id="caixa_postal_notificacao"
            title="Caixa Postal (RFB)"
            panel={this.caixaPostalCorpo()}
          />

          <Tab
            id="dec_sefa_notificacao"
            title="DEC SEFA"
            panel={this.decSefaCorpo()}
          />

          <Tab
            id="det_notificacao"
            title="DET"
            panel={this.detCorpo()}
          />

          <Tab
            id="parcelamentos_notificacao"
            title="Parcelamentos"
            panel={this.parcelamentosCorpo()}
          />

          {e.simples_nacional &&
            <Tab
              id="pgdas_notificacao"
              title="PGDAS"
              panel={this.pgdasCorpo()}
            />
          }

          <Tab
            id="calculos_notificacao"
            title="Cálculos"
            panel={this.calculosCorpo()}
          />

          <Tab
            id="cnds_notificacao"
            title="CNDS"
            panel={this.cndsCorpo()}
          />

        </Tabs>

      </MDBCol>
    );
  }
}
